/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { createLogger } from '@/lib/logger';

const logger = createLogger();

/** DOM id of the injected Adobe Launch embed, used to dedupe injection. */
export const ADOBE_LAUNCH_SCRIPT_ID = 'adobe-launch-embed';

/**
 * Module-level cache so a remount (or a second consent callback) reuses the in-flight injection
 * instead of adding a second embed. Adobe Launch is not safe to load twice — the second copy
 * re-registers every rule and double-fires page views.
 */
let injection: Promise<boolean> | null = null;

/** Reset the injection cache. Test-only. */
export function resetAdobeLaunchInjectionForTests(): void {
    injection = null;
}

/** Whether the Launch embed has been injected on this page. */
export function isAdobeLaunchLoaded(): boolean {
    return typeof document !== 'undefined' && document.getElementById(ADOBE_LAUNCH_SCRIPT_ID) !== null;
}

/**
 * Inject the Adobe Launch (Tags) embed.
 *
 * Launch used to be a nonce'd `<script>` in `<head>`, rendered unconditionally. It moved here when
 * OneTrust took over consent (UCR-2991): Adobe Analytics is a Performance-category tag, so the
 * embed is withheld until the shopper grants C0002 and the consent bridge calls this.
 *
 * Nothing is lost by loading late. `root.tsx` still initialises `window.adobeDataLayer` in `<head>`,
 * so page views pushed before consent are queued in that array and replayed by Launch the moment it
 * initialises — the same mechanism that made the original `async` embed safe.
 *
 * The injected script deliberately carries **no nonce**: the root loader's nonce is only valid for
 * the SSR-rendered HTML, and on a client navigation the loader returns a fresh nonce with no
 * accompanying CSP header. `assets.adobedtm.com` is allowlisted in `script-src` instead, same as
 * Attentive and Sierra.
 *
 * @returns Whether the embed finished loading. Never rejects.
 */
export function injectAdobeLaunch(src: string): Promise<boolean> {
    if (typeof document === 'undefined' || !src) {
        return Promise.resolve(false);
    }

    injection ??= new Promise<boolean>((resolve) => {
        if (isAdobeLaunchLoaded()) {
            resolve(true);
            return;
        }

        const script = document.createElement('script');
        script.id = ADOBE_LAUNCH_SCRIPT_ID;
        script.async = true;
        script.src = src;
        script.onload = () => resolve(true);
        script.onerror = () => {
            logger.error('[OneTrust] Adobe Launch embed failed to load', { src });
            resolve(false);
        };

        document.head.appendChild(script);
    });

    return injection;
}
